import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Small } from '@/components/Typography';

const THUMBNAIL_WIDTH = 100;

interface PdfPageThumbnailsProps {
  pdfDoc: any;
  numPages: number;
  currentPage: number;
  fieldsPerPage: Record<number, number>;
  onPageSelect: (page: number) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const PdfPageThumbnails: React.FC<PdfPageThumbnailsProps> = ({
  pdfDoc,
  numPages,
  currentPage,
  fieldsPerPage,
  onPageSelect,
  collapsed,
  onToggleCollapse,
}) => {
  const [thumbnails, setThumbnails] = useState<Map<number, string>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const renderingRef = useRef<Set<number>>(new Set());

  const renderThumbnail = useCallback(
    async (pageNum: number) => {
      if (
        !pdfDoc ||
        thumbnails.has(pageNum) ||
        renderingRef.current.has(pageNum)
      )
        return;
      renderingRef.current.add(pageNum);
      try {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });
        const thumbScale = THUMBNAIL_WIDTH / viewport.width;
        const scaledViewport = page.getViewport({ scale: thumbScale });
        const canvas = document.createElement('canvas');
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        setThumbnails((prev) => {
          const next = new Map(prev);
          next.set(pageNum, dataUrl);
          return next;
        });
      } finally {
        renderingRef.current.delete(pageNum);
      }
    },
    [pdfDoc, thumbnails],
  );

  useEffect(() => {
    if (!pdfDoc || collapsed) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const pageNum = Number((entry.target as HTMLElement).dataset.page);
            if (pageNum) renderThumbnail(pageNum);
          }
        }
      },
      { root: containerRef.current, rootMargin: '100px' },
    );
    const observer = observerRef.current;
    thumbRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [pdfDoc, collapsed, numPages, renderThumbnail]);

  const setThumbRef = useCallback(
    (pageNum: number, el: HTMLDivElement | null) => {
      if (el) {
        thumbRefs.current.set(pageNum, el);
        observerRef.current?.observe(el);
      } else {
        const existing = thumbRefs.current.get(pageNum);
        if (existing) observerRef.current?.unobserve(existing);
        thumbRefs.current.delete(pageNum);
      }
    },
    [],
  );

  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div
      style={{
        position: 'relative',
        width: collapsed ? '36px' : '156px',
        minWidth: collapsed ? '36px' : '156px',
        transition: 'width 0.2s, min-width 0.2s',
        borderRight: '1px solid #d6deeb',
        background: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: '8px',
          padding: collapsed ? '12px 4px' : '12px 12px 10px',
          borderBottom: '1px solid #d6deeb',
          background: '#f8fafc',
        }}
      >
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <Small style={{ color: '#0f172a', fontWeight: 700 }}>Pages</Small>
            <Small style={{ color: '#64748b', display: 'block' }}>
              {numPages} total
            </Small>
          </div>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '999px',
            border: '1px solid #d6deeb',
            background: '#fff',
            color: '#475569',
            cursor: 'pointer',
            padding: 0,
          }}
          title={collapsed ? 'Show page thumbnails' : 'Hide page thumbnails'}
        >
          {collapsed ? '\u25B6' : '\u25C0'}
        </button>
      </div>

      {!collapsed && (
        <div
          ref={containerRef}
          style={{
            padding: '10px 10px 16px',
            overflowY: 'auto',
            maxHeight: '100%',
          }}
        >
          {pages.map((pageNum) => {
            const isCurrent = pageNum === currentPage;
            const hasFields = (fieldsPerPage[pageNum] || 0) > 0;
            const thumbUrl = thumbnails.get(pageNum);

            return (
              <div
                key={pageNum}
                ref={(el) => setThumbRef(pageNum, el)}
                data-page={pageNum}
                onClick={() => onPageSelect(pageNum)}
                style={{
                  padding: '6px 2px',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    border: isCurrent
                      ? '2px solid #2563eb'
                      : '1px solid #d6deeb',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: '60px',
                    background: '#fff',
                    boxShadow: isCurrent
                      ? '0 12px 30px rgba(37, 99, 235, 0.16)'
                      : '0 4px 14px rgba(15, 23, 42, 0.05)',
                  }}
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={`Page ${pageNum}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                  ) : (
                    <div
                      style={{
                        height: '80px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Small style={{ color: '#999' }}>{pageNum}</Small>
                    </div>
                  )}
                  {hasFields && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        minWidth: '20px',
                        height: '20px',
                        padding: '0 6px',
                        borderRadius: '999px',
                        backgroundColor: '#dbeafe',
                        color: '#1d4ed8',
                        fontSize: '10px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title={`${fieldsPerPage[pageNum]} field(s)`}
                    >
                      {fieldsPerPage[pageNum]}
                    </div>
                  )}
                </div>
                <Small
                  style={{
                    textAlign: 'center',
                    display: 'block',
                    marginTop: '6px',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent ? '#2563eb' : '#666',
                  }}
                >
                  Page {pageNum}
                </Small>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PdfPageThumbnails;
